import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Typography } from "@circle-react-uikit/Typography";
import { PreviewContent } from "./PreviewContent";
import { BuildingBlocks } from "./components/BuildingBlocks";
import { UserInfo } from "./components/UserInfo";
import { useCommunityPreviewData } from "./hooks/useCommunityPreviewData";
import { PreviewCard } from "./layout/PreviewCard";
import { PreviewField } from "./layout/PreviewField";
import { PreviewSection } from "./layout/PreviewSection";
import { PreviewTitle } from "./layout/PreviewTitle";
import { PreviewUserSection } from "./layout/PreviewUserSection";

export interface CommunityPreviewProps {
  animateBuildingBlock?: string;
  animateField?: "customerJourneyStage" | "achieveWithCircle";
  animateOnMount?: boolean;
  buildingBlocks?: string[];
  communityName: string;
  shimmerTitle?: boolean;
  showSkeletonForAchieveWithCircle?: boolean;
  showSkeletonForCustomerJourneyStage?: boolean;
}

export const CommunityPreview = ({
  communityName,
  buildingBlocks: customBuildingBlocks,
  animateOnMount,
  animateField,
  shimmerTitle = false,
  showSkeletonForCustomerJourneyStage,
  showSkeletonForAchieveWithCircle,
}: CommunityPreviewProps) => {
  const localeScope =
    "authentication_app.new_onboarding_flow_april_2025.community_preview";

  const form = useFormContext();
  const watchedSlug = form?.watch("community[slug]");
  const watchedName = form?.watch("community[name]");

  const {
    customerJourneyStage,
    achieveWithCircle,
    buildingBlocks,
    userInitials,
    fullName,
    email,
    avatar_url,
    shouldAnimateCustomerJourneyStage,
    shouldAnimateAchieveWithCircle,
    slugToUse,
    nameToUse,
    hasUserModifiedName,
  } = useCommunityPreviewData(
    communityName,
    customBuildingBlocks,
    animateField,
    watchedSlug,
    watchedName,
  );

  const isCustomerJourneyStageFieldVisible =
    showSkeletonForCustomerJourneyStage || !!customerJourneyStage;

  const shouldShowCustomerJourneyStageSkeleton =
    showSkeletonForCustomerJourneyStage || !customerJourneyStage;

  const isAchieveWithCircleFieldVisible =
    showSkeletonForAchieveWithCircle || !!achieveWithCircle;

  const shouldShowAchieveWithCircleSkeleton =
    showSkeletonForAchieveWithCircle || !achieveWithCircle;

  return (
    <PreviewCard animateOnMount={animateOnMount}>
      <PreviewSection>
        <PreviewTitle
          subtitle={t(`${localeScope}.title`)}
          title={nameToUse}
          shimmer={shimmerTitle && !hasUserModifiedName}
        />
        {slugToUse && (
          <PreviewField label={t(`${localeScope}.community_url`)}>
            <Typography.BodySm color="text-default">
              {`https://${slugToUse}.circle.so`}
            </Typography.BodySm>
          </PreviewField>
        )}
        {isCustomerJourneyStageFieldVisible && (
          <PreviewField label={t(`${localeScope}.journey_stage`)}>
            <PreviewContent
              shouldShowSkeleton={shouldShowCustomerJourneyStageSkeleton}
              value={customerJourneyStage}
              shouldAnimate={shouldAnimateCustomerJourneyStage}
            />
          </PreviewField>
        )}
        {isAchieveWithCircleFieldVisible && (
          <PreviewField label={t(`${localeScope}.achieve_with_circle`)}>
            <PreviewContent
              shouldShowSkeleton={shouldShowAchieveWithCircleSkeleton}
              value={achieveWithCircle}
              shouldAnimate={shouldAnimateAchieveWithCircle}
            />
          </PreviewField>
        )}
        {buildingBlocks.length > 0 && (
          <PreviewField label={t(`${localeScope}.blocks_title`)}>
            <BuildingBlocks blocks={buildingBlocks} />
          </PreviewField>
        )}
      </PreviewSection>
      <PreviewUserSection>
        <UserInfo
          fullName={fullName}
          email={email}
          initials={userInitials}
          avatar_url={avatar_url}
        />
      </PreviewUserSection>
    </PreviewCard>
  );
};
